import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { apiRequest } from "../../../api/http";
import {
  WorkRecordMonthly,
  WorkRecordWeekly,
  WorkRecordDaily,
  WorkRecordHourly,
} from "../../../types/admin";
import { getWeekRangeLabels } from "../../../utils/workCalendar";
import { parseWorkRecordListResponse, normalizeHourlyEmployees } from "../../../utils/workRecords";
import { formatHoursFromMinutes } from "../../../utils/workTimeDisplay";

function monthlyAllowanceCellText(item: WorkRecordMonthly): string {
  const n = (v: unknown) => {
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    if (typeof v === "string" && v.trim() !== "") {
      const x = Number(v);
      return Number.isNaN(x) ? 0 : x;
    }
    return 0;
  };
  const other = n(item.other_settlement_amount);
  const weekly = n(item.total_weekly_holiday_allowance);
  return `기타: ${other.toLocaleString()}\n주휴: ${weekly.toLocaleString()}`;
}

interface WorkStatusTabProps {
  branchId: number;
}

type TabType = "monthly" | "weekly" | "daily" | "hourly";

const WorkStatusTab: React.FC<WorkStatusTabProps> = ({ branchId }) => {
  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [month, setMonth] = useState<number>(today.getMonth() + 1);
  const [activeTab, setActiveTab] = useState<TabType>("monthly");
  
  const [loading, setLoading] = useState(false);
  const [monthlyData, setMonthlyData] = useState<WorkRecordMonthly[]>([]);
  const [weeklyData, setWeeklyData] = useState<WorkRecordWeekly[]>([]);
  const [dailyData, setDailyData] = useState<WorkRecordDaily[]>([]);
  const [hourlyData, setHourlyData] = useState<WorkRecordHourly[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [weekStartWeekday, setWeekStartWeekday] = useState<number>(1);

  const weekHeaders = getWeekRangeLabels(year, month, weekStartWeekday);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === "monthly") {
        const raw = await apiRequest<unknown>(
          `/admin/branches/${branchId}/work-records/monthly?year=${year}&month=${month}`
        );
        const { items, week_start_weekday } = parseWorkRecordListResponse<WorkRecordMonthly>(raw);
        setMonthlyData(items);
        setWeekStartWeekday(week_start_weekday);
      } else if (activeTab === "weekly") {
        const raw = await apiRequest<unknown>(
          `/admin/branches/${branchId}/work-records/weekly?year=${year}&month=${month}`
        );
        // The items are WorkRecordWeekly
        const { items, week_start_weekday } = parseWorkRecordListResponse<WorkRecordWeekly>(raw);
        setWeeklyData(items);
        setWeekStartWeekday(week_start_weekday);
      } else if (activeTab === "daily") {
        const raw = await apiRequest<unknown>(
          `/admin/branches/${branchId}/work-records/daily?year=${year}&month=${month}`
        );
        const { items, week_start_weekday } = parseWorkRecordListResponse<WorkRecordDaily>(raw);
        setDailyData(
          items.map((row) => ({
            ...row,
            employees: Array.isArray(row.employees) ? row.employees : [],
          }))
        );
        setWeekStartWeekday(week_start_weekday);
      } else if (activeTab === "hourly") {
        const raw = await apiRequest<unknown>(
          `/admin/branches/${branchId}/work-records/hourly?year=${year}&month=${month}`
        );
        const { items, week_start_weekday } = parseWorkRecordListResponse<WorkRecordHourly>(raw);
        // hourly_employees was added, fallback if missing
        setHourlyData(
          items.map((row) => ({
            ...row,
            hourly_employees: normalizeHourlyEmployees((row as any).hourly_employees),
          }))
        );
        setWeekStartWeekday(week_start_weekday);
      }
    } catch (err: any) {
      setError(err.message || "근무현황 데이터를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [branchId, year, month, activeTab]);

  const handleExcelDownload = () => {
    const wb = XLSX.utils.book_new();
    let sheetName = "";
    let fileName = "";
    let wsData: (string | number)[][] = [];

    if (activeTab === "monthly") {
      sheetName = "월별근무현황";
      fileName = `${year}년_${month}월_월별근무현황.xlsx`;
      const headers = [
        "번호",
        "직원구분",
        "이름(근로자별)",
        "총 근무시간(시간)",
        "근무일수",
        "적용시급",
        "총금액",
        "기타정산금/총주휴수당",
        "소정 근로시간(시간)",
        "전월(28~31) 근무시간(시간)",
        ...weekHeaders.map((h) => `${h}\n근무시간(시간)`),
        "시급",
      ];
      wsData = [headers];
      monthlyData.forEach((item, idx) => {
        wsData.push([
          idx + 1,
          item.employee_type || "-",
          item.employee_name,
          formatHoursFromMinutes(item.total_work_minutes, "0.0"),
          item.total_work_days ?? 0,
          item.hourly_wage != null ? item.hourly_wage.toLocaleString() : "-",
          (item.total_amount ?? 0).toLocaleString(),
          monthlyAllowanceCellText(item),
          formatHoursFromMinutes(item.contract_work_minutes),
          formatHoursFromMinutes(item.prev_month_work_minutes),
          formatHoursFromMinutes(item.week1_work_minutes),
          formatHoursFromMinutes(item.week2_work_minutes),
          formatHoursFromMinutes(item.week3_work_minutes),
          formatHoursFromMinutes(item.week4_work_minutes),
          formatHoursFromMinutes(item.week5_work_minutes),
          item.base_hourly_wage != null ? item.base_hourly_wage.toLocaleString() : "-",
        ]);
      });
    } else if (activeTab === "weekly") {
      sheetName = "주차별근무현황";
      fileName = `${year}년_${month}월_주차별근무현황.xlsx`;
      const empNames = weeklyData.map(e => e.employee_name);
      wsData = [
        ["이름", ...empNames],
        ["구분", ...weeklyData.map(e => e.employee_type || "-")],
        ["소정 근로시간", ...weeklyData.map(e => formatHoursFromMinutes(e.contract_work_minutes))],
        ["전월", ...weeklyData.map(e => formatHoursFromMinutes(e.prev_month_work_minutes))]
      ];
      [1, 2, 3, 4, 5].forEach(week => {
        wsData.push([`${week}주차`, ...weeklyData.map(e => formatHoursFromMinutes((e as any)[`week${week}_work_minutes`]))]);
        wsData.push(["결근", ...weeklyData.map(e => formatHoursFromMinutes((e as any)[`week${week}_absent_minutes`]))]);
      });
      wsData.push(["근무시간계(전월 제외)", ...weeklyData.map(e => formatHoursFromMinutes(e.total_work_minutes_current_month))]);
      wsData.push(["결근시간계(전월 제외)", ...weeklyData.map(e => formatHoursFromMinutes(e.total_absent_minutes_current_month))]);
    } else if (activeTab === "daily") {
      sheetName = "일별근무현황";
      fileName = `${year}년_${month}월_일별근무현황.xlsx`;
      const dailyUniqueEmployees = Array.from(new Set(dailyData.flatMap(d => d.employees.map(e => e.employee_name))));
      wsData = [["주차", "요일", "일", ...dailyUniqueEmployees.map(name => `(T)${name}`)]];
      dailyData.forEach((item, idx) => {
        const isFirstOfWeek = idx === 0 || dailyData[idx - 1].week_number !== item.week_number;
        const weekStr = isFirstOfWeek ? `${item.week_number}주차` : "";
        const dayNum = parseInt(item.work_date.split('-')[2], 10);
        wsData.push([
          weekStr,
          item.day_of_week,
          dayNum || item.work_date,
          ...dailyUniqueEmployees.map(empName => {
            const emp = item.employees.find(e => e.employee_name === empName);
            return emp ? formatHoursFromMinutes(emp.work_minutes, "-") : "-";
          })
        ]);
      });
    } else {
      sheetName = "시간별근무현황";
      fileName = `${year}년_${month}월_시간별근무현황.xlsx`;
      wsData = [["주차", "요일", "일", "근무시간", "중복인시", ...Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}시`)]];
      hourlyData.forEach((item, idx) => {
        const isFirstOfWeek = idx === 0 || hourlyData[idx - 1].week_number !== item.week_number;
        const weekStr = isFirstOfWeek ? `${item.week_number}주차` : "";
        const dayNum = parseInt(item.work_date.split('-')[2], 10);
        wsData.push([
          weekStr,
          item.day_of_week,
          dayNum || item.work_date,
          formatHoursFromMinutes(item.total_work_minutes, "0.0"),
          formatHoursFromMinutes(item.overlapping_minutes, "0.0"),
          ...item.hourly_employees.map(emps => emps.join(", "))
        ]);
      });
      const hourlyTotalWork = hourlyData.reduce((acc, curr) => acc + curr.total_work_minutes, 0);
      const hourlyTotalOverlap = hourlyData.reduce((acc, curr) => acc + curr.overlapping_minutes, 0);
      wsData.push(["근무시간 계", "", "", formatHoursFromMinutes(hourlyTotalWork, "0.0"), "자동계산", ...Array.from({ length: 24 }, () => "")]);
      wsData.push(["중복인시계", "", "", "자동계산", formatHoursFromMinutes(hourlyTotalOverlap, "0.0"), ...Array.from({ length: 24 }, () => "")]);
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, fileName);
  };

  const dailyUniqueEmployees = Array.from(new Set(dailyData.flatMap(d => d.employees.map(e => e.employee_name))));
  const dailyWeekCounts = dailyData.reduce((acc, curr) => {
    acc[curr.week_number] = (acc[curr.week_number] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);
  const renderedDailyWeekNum = new Set<number>();

  const hourlyWeekCounts = hourlyData.reduce((acc, curr) => {
    acc[curr.week_number] = (acc[curr.week_number] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);
  const renderedHourlyWeekNum = new Set<number>();
  const hourlyTotalWork = hourlyData.reduce((acc, curr) => acc + curr.total_work_minutes, 0);
  const hourlyTotalOverlap = hourlyData.reduce((acc, curr) => acc + curr.overlapping_minutes, 0);

  return (
    <div className="work-status-tab">
      <div className="filter-bar" style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontWeight: '500', color: '#666874' }}>년월</label>
          <input 
            type="month" 
            value={`${year}-${month.toString().padStart(2, '0')}`} 
            onChange={(e) => {
              const [y, m] = e.target.value.split('-');
              if (y && m) {
                setYear(parseInt(y));
                setMonth(parseInt(m));
              }
            }}
            style={{ padding: '8px', border: '1px solid #e7e8ef', borderRadius: '4px' }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div className="sub-tabs" style={{ display: 'flex' }}>
          {(['monthly', 'weekly', 'daily', 'hourly'] as TabType[]).map((tab, index) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '8px 24px',
                border: '1px solid #d9d9d9',
                borderLeft: index === 0 ? '1px solid #d9d9d9' : 'none',
                backgroundColor: activeTab === tab ? '#333' : '#fff',
                color: activeTab === tab ? '#fff' : '#333',
                fontWeight: activeTab === tab ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              {tab === 'monthly' && '월별'}
              {tab === 'weekly' && '주차별'}
              {tab === 'daily' && '일별'}
              {tab === 'hourly' && '시간별'}
            </button>
          ))}
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            onClick={handleExcelDownload}
            style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #d9d9d9', cursor: 'pointer' }}
          >
            엑셀 다운로드
          </button>
        </div>
      </div>

      {loading && <p>데이터를 불러오는 중입니다...</p>}
      {error && <p className="error-text">{error}</p>}

      {!loading && !error && (
        <div className="table-shell" style={{ overflowX: 'auto' }}>
          <table className="admin-table compact" style={{ whiteSpace: 'nowrap', borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
            {activeTab === 'monthly' && (
              <>
                <thead style={{ backgroundColor: '#f5f5f5' }}>
                  <tr>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>번호</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>직원구분</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>이름<br/><span style={{ fontSize: '11px', fontWeight: 400 }}>(근로자별)</span></th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>총 근무시간<br/><span style={{ fontSize: '11px', fontWeight: 400 }}>(시간)</span></th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>근무일수</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>적용시급</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>총금액</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>기타정산금/<br/>총주휴수당</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>소정<br/>근로시간<br/><span style={{ fontSize: '11px', fontWeight: 400 }}>(시간)</span></th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>전월<br/>(28~31)<br/><span style={{ fontSize: '11px', fontWeight: 400 }}>근무(시간)</span></th>
                    {weekHeaders.map((wh) => (
                      <th key={wh} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                        {wh}
                        <br />
                        <span style={{ fontSize: '11px', fontWeight: 400 }}>근무(시간)</span>
                      </th>
                    ))}
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>시급</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.map((item, idx) => (
                    <tr key={item.employee_id}>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{idx + 1}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.employee_type || '-'}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.employee_name}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(item.total_work_minutes, "0.0")}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.total_work_days ?? 0}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.hourly_wage?.toLocaleString() || '-'}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.total_amount?.toLocaleString() || 0}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center', whiteSpace: 'pre-line' }}>{monthlyAllowanceCellText(item)}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(item.contract_work_minutes)}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(item.prev_month_work_minutes)}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(item.week1_work_minutes)}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(item.week2_work_minutes)}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(item.week3_work_minutes)}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(item.week4_work_minutes)}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(item.week5_work_minutes)}</td>
                      <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.base_hourly_wage?.toLocaleString() || '-'}</td>
                    </tr>
                  ))}
                  {monthlyData.length === 0 && (
                    <tr><td colSpan={16} className="empty-row" style={{ border: '1px solid #e0e0e0', padding: '16px' }}>데이터가 없습니다.</td></tr>
                  )}
                </tbody>
              </>
            )}

            {activeTab === 'weekly' && (
              <>
                <thead style={{ backgroundColor: '#f5f5f5' }}>
                  <tr>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>이름</th>
                    {weeklyData.map(emp => (
                      <th key={emp.employee_id} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                        {emp.employee_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th style={{ backgroundColor: '#fdfdfd', border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>구분</th>
                    {weeklyData.map(emp => (
                      <td key={emp.employee_id} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                        {emp.employee_type || '-'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th style={{ backgroundColor: '#fdfdfd', border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>소정 근로시간</th>
                    {weeklyData.map(emp => (
                      <td key={emp.employee_id} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                        {formatHoursFromMinutes(emp.contract_work_minutes)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th style={{ backgroundColor: '#fdfdfd', border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>전월</th>
                    {weeklyData.map(emp => (
                      <td key={emp.employee_id} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                        {formatHoursFromMinutes(emp.prev_month_work_minutes)}
                      </td>
                    ))}
                  </tr>
                  {[1, 2, 3, 4, 5].map(week => (
                    <React.Fragment key={week}>
                      <tr>
                        <th style={{ backgroundColor: '#fdfdfd', border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{week}주차</th>
                        {weeklyData.map(emp => (
                          <td key={emp.employee_id} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                            {formatHoursFromMinutes((emp as any)[`week${week}_work_minutes`])}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: '#fdfdfd', border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>결근</th>
                        {weeklyData.map(emp => (
                          <td key={emp.employee_id} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                            {formatHoursFromMinutes((emp as any)[`week${week}_absent_minutes`])}
                          </td>
                        ))}
                      </tr>
                    </React.Fragment>
                  ))}
                  <tr>
                    <th style={{ backgroundColor: '#fdfdfd', border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>근무시간계<br/>(전월 제외)</th>
                    {weeklyData.map(emp => (
                      <td key={emp.employee_id} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                        {formatHoursFromMinutes(emp.total_work_minutes_current_month)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th style={{ backgroundColor: '#fdfdfd', border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>결근시간계<br/>(전월 제외)</th>
                    {weeklyData.map(emp => (
                      <td key={emp.employee_id} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                        {formatHoursFromMinutes(emp.total_absent_minutes_current_month)}
                      </td>
                    ))}
                  </tr>
                  {weeklyData.length === 0 && (
                    <tr><td colSpan={2} className="empty-row" style={{ border: '1px solid #e0e0e0', padding: '16px' }}>데이터가 없습니다.</td></tr>
                  )}
                </tbody>
              </>
            )}

            {activeTab === 'daily' && (
              <>
                <thead style={{ backgroundColor: '#f5f5f5' }}>
                  <tr>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>주차</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>요일</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>일</th>
                    {dailyUniqueEmployees.map(empName => (
                      <th key={empName} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>(T){empName}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dailyData.map((item, idx) => {
                    const isFirstOfWeek = !renderedDailyWeekNum.has(item.week_number);
                    if (isFirstOfWeek) renderedDailyWeekNum.add(item.week_number);
                    return (
                      <tr key={idx}>
                        {isFirstOfWeek && (
                          <td rowSpan={dailyWeekCounts[item.week_number]} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: '#fdfdfd' }}>
                            {item.week_number}주차
                          </td>
                        )}
                        <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.day_of_week}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{parseInt(item.work_date.split('-')[2], 10) || item.work_date}</td>
                        {dailyUniqueEmployees.map(empName => {
                          const emp = item.employees.find(e => e.employee_name === empName);
                          return (
                            <td key={empName} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                              {emp ? formatHoursFromMinutes(emp.work_minutes, "-") : "-"}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {dailyData.length === 0 && (
                    <tr><td colSpan={3 + dailyUniqueEmployees.length} className="empty-row" style={{ border: '1px solid #e0e0e0', padding: '16px' }}>데이터가 없습니다.</td></tr>
                  )}
                </tbody>
              </>
            )}

            {activeTab === 'hourly' && (
              <>
                <thead style={{ backgroundColor: '#f5f5f5' }}>
                  <tr>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>주차</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>요일</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>일</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>근무시간</th>
                    <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>중복인시</th>
                    {Array.from({length: 24}).map((_, i) => (
                      <th key={i} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                        {String(i).padStart(2, '0')}시
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hourlyData.map((item, idx) => {
                    const isFirstOfWeek = !renderedHourlyWeekNum.has(item.week_number);
                    if (isFirstOfWeek) renderedHourlyWeekNum.add(item.week_number);
                    return (
                      <tr key={idx}>
                        {isFirstOfWeek && (
                          <td rowSpan={hourlyWeekCounts[item.week_number]} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: '#fdfdfd' }}>
                            {item.week_number}주차
                          </td>
                        )}
                        <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.day_of_week}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{parseInt(item.work_date.split('-')[2], 10) || item.work_date}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(item.total_work_minutes, "0.0")}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(item.overlapping_minutes, "0.0")}</td>
                        {item.hourly_employees.map((emps, i) => (
                          <td key={i} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{emps.join(', ')}</td>
                        ))}
                      </tr>
                    );
                  })}
                  {hourlyData.length > 0 && (
                    <>
                      <tr>
                        <td colSpan={3} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center', backgroundColor: '#fdfdfd', fontWeight: 'bold' }}>근무시간 계</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(hourlyTotalWork, "0.0")}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>자동계산</td>
                        <td colSpan={24} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}></td>
                      </tr>
                      <tr>
                        <td colSpan={3} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center', backgroundColor: '#fdfdfd', fontWeight: 'bold' }}>중복인시계</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>자동계산</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{formatHoursFromMinutes(hourlyTotalOverlap, "0.0")}</td>
                        <td colSpan={24} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}></td>
                      </tr>
                    </>
                  )}
                  {hourlyData.length === 0 && (
                    <tr><td colSpan={29} className="empty-row" style={{ border: '1px solid #e0e0e0', padding: '16px' }}>데이터가 없습니다.</td></tr>
                  )}
                </tbody>
              </>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default WorkStatusTab;
